import { useEffect, useRef, useState, type Dispatch, type RefObject, type SetStateAction } from "react";
import type { CellSightline } from "../../../lib/encounter/combatInspector";

export type AttackHoverState = {
  attackHover: Set<string> | null;
  setAttackHover: Dispatch<SetStateAction<Set<string> | null>>;
  attackHoverCellRef: RefObject<string | null>;
  attackAffectedRef: RefObject<Set<string> | null>;
  attackLosExcludedHover: Set<string> | null;
  setAttackLosExcludedHover: Dispatch<SetStateAction<Set<string> | null>>;
  attackLosExcludedRef: RefObject<Set<string> | null>;
  attackRangeRef: RefObject<Set<string> | null>;
  reachableRef: RefObject<Set<string> | null>;
  cellSightlinesRef: RefObject<CellSightline[] | null>;
};

/**
 * The hovered attack-affected template (the red AoE preview) and its LoS-excluded markers, plus the
 * live mirrors of the three wash PROPS the TEST-ONLY debug hook reads. Every ref here exists because
 * `__combatBoardDebug` is populated once at mount and would otherwise read a stale closure.
 */
export function useAttackHover({
  attackRange,
  reachable,
  cellSightlines,
}: {
  attackRange: Set<string> | null;
  reachable: Set<string> | null;
  cellSightlines: CellSightline[] | null;
}): AttackHoverState {
  // The hovered attack-affected template (the red AoE preview): the cell-key set a committed shot
  // would hit, recomputed (via `attackAffected`) only when the cursor crosses into a new cell while an
  // attack is armed, and cleared on leave / disarm. The attack twin of `movePath`, but driven by free
  // hover rather than a drag. `attackHoverCellRef` coalesces the recompute to cell-change granularity.
  const [attackHover, setAttackHover] = useState<Set<string> | null>(null);
  const attackHoverCellRef = useRef<string | null>(null);
  // Mirrors `attackHover` (kept live alongside the state, same reason as `attackLosExcludedRef` below)
  // so the TEST-ONLY debug hook can read the CURRENT affected-template key set without going stale in
  // its one-time mount closure — the e2e proof for reads this
  // to assert the previewed cell set against the resolver's true hit set.
  const attackAffectedRef = useRef<Set<string> | null>(null);
  // The hovered template's LoS-excluded cells — recomputed in
  // lockstep with `attackHover` (same cell-change coalescing), a SEPARATE set so a sheltered token's
  // marker draws on top of (never in place of) the red affected wash. Also mirrored into a ref (kept
  // live alongside the state) so the TEST-ONLY debug hook below can read the CURRENT value without
  // going stale in its one-time mount closure.
  const [attackLosExcludedHover, setAttackLosExcludedHover] = useState<Set<string> | null>(null);
  const attackLosExcludedRef = useRef<Set<string> | null>(null);
  // Drop the hovered AoE preview the moment the attack disarms (the reach wash vanishes with
  // `attackRange`; the affected template must clear with it, or a stale red wedge lingers).
  useEffect(() => {
    if (!attackRange) {
      setAttackHover(null);
      attackHoverCellRef.current = null;
      attackAffectedRef.current = null;
      setAttackLosExcludedHover(null);
      attackLosExcludedRef.current = null;
    }
  }, [attackRange]);
  // Mirrors the `attackRange` PROP into a ref (same reason as `attackAffectedRef`/`attackLosExcludedRef`
  // above) so the TEST-ONLY debug hook below — populated once at mount — can read its CURRENT value
  // rather than the stale one from that one-time closure. `attackRange` is a plain prop (not a
  // hover-driven recompute like the two above), but the SAME staleness problem applies: it changes on
  // every `directedDeployDraftComplete`-bound pick (`jericho-directed-placement` D3/D4/D6's legal-cell
  // wash, recomputed after each pick while `activate_sites` is armed) and the mount closure would never
  // see those updates without this mirror.
  const attackRangeRef = useRef<Set<string> | null>(null);
  useEffect(() => {
    attackRangeRef.current = attackRange;
  }, [attackRange]);
  // Mirrors the `reachable` PROP (same reason/shape as `attackRangeRef` above) so the TEST-ONLY
  // debug hook's `getMoveRangeKeys` can read the CURRENT movement-range wash's cell-key set —
  //'s browser proof that a player's client genuinely honors a
  // `blocksMovement` wall (not just that SOME wash rendered — `getMoveRangeCount` alone can't tell
  // a full reach from a walled-off one apart, only the actual key SET can).
  const reachableRef = useRef<Set<string> | null>(null);
  useEffect(() => {
    reachableRef.current = reachable;
  }, [reachable]);
  // Mirrors the `cellSightlines` PROP (same reason/shape as `attackRangeRef` above) so the
  // TEST-ONLY debug hook's `getCoverAt` can read the CURRENT per-cell cover advisory —
  //'s browser proof that a player's client's cover advisory
  // genuinely reads a `cover`-granting object (a crate) rather than silently reporting "none"
  // because the object's `objectProfile` never reached this wire.
  const cellSightlinesRef = useRef<CellSightline[] | null>(null);
  useEffect(() => {
    cellSightlinesRef.current = cellSightlines;
  }, [cellSightlines]);

  return {
    attackHover,
    setAttackHover,
    attackHoverCellRef,
    attackAffectedRef,
    attackLosExcludedHover,
    setAttackLosExcludedHover,
    attackLosExcludedRef,
    attackRangeRef,
    reachableRef,
    cellSightlinesRef,
  };
}
